import { useEffect, useMemo, useRef, useState } from "react";
import SearchInput from "./shared/SearchInput";
import { fetchTestNTagStaff, reassignTestNTag } from "../utils/api";


const F = { id: 0, leadStatus: 1, unitStatus: 2, client: 5, licence: 6, item: 7, licenceNumber: 10 };

/* One colour per assignee — drives checkbox borders + button accents */
const COLORS = ["#22d3ee", "#a78bfa", "#4ade80", "#fbbf24", "#f472b6", "#60a5fa", "#f87171", "#34d399"];

const ACTIVE_RING = "#22d3ee"; // permanent focus ring on the active button

const TABLE_COLS = "44px 1.3fr 1.3fr 1.1fr 0.85fr 0.85fr";

const staffKey = (p) => `${p.name}::${p.job ?? ""}`;

/* ── styles (same visual language as ComplianceExpiryModal) ── */
const S = {
  backdrop: {
    position: "fixed", inset: 0, top: "var(--fr-modal-top, 0px)", background: "rgba(2,6,23,0.7)", zIndex: 1100,
    display: "flex", alignItems: "flex-start", justifyContent: "center",
    padding: "40px 16px", overflowY: "auto",
  },
  modal: {
    position: "relative", maxHeight: "calc(var(--fr-modal-max, 82vh) - 76px)", overflowY: "auto", overscrollBehavior: "contain", width: "100%", maxWidth: 880,
    background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
    border: "1px solid rgba(34,211,238,0.25)", borderRadius: 16,
    padding: "18px 18px 14px",
  },
  panelAccent: {
    position: "absolute", top: 0, left: 0, right: 0, height: 3,
    background: "linear-gradient(90deg, #22d3ee, #3b82f6, #8b5cf6)",
  },
  head: { position: "sticky", top: 0, zIndex: 5, background: "#1e293b", boxShadow: "0 -18px 0 0 #1e293b",  display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 },
  title: { margin: 0, fontSize: "1rem", fontWeight: 700, color: "#f1f5f9" },
  closeX: {
    background: "none", border: "none", color: "rgba(255,255,255,0.6)",
    fontSize: "1.4rem", lineHeight: 1, cursor: "pointer",
  },
  eyebrow: {
    fontSize: "0.62rem", letterSpacing: "0.14em", textTransform: "uppercase",
    color: "#22d3ee", fontWeight: 700,
  },
  chipRow: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 },
  chip: (active, danger) => ({
    borderRadius: 999, padding: "6px 14px", fontSize: "0.78rem", fontWeight: 700,
    cursor: "pointer", whiteSpace: "nowrap",
    background: active ? "rgba(34,211,238,0.18)" : danger ? "rgba(139,92,246,0.12)" : "rgba(255,255,255,0.05)",
    border: `1px solid ${active ? "rgba(34,211,238,0.6)" : danger ? "rgba(139,92,246,0.45)" : "rgba(255,255,255,0.14)"}`,
    color: active ? "#22d3ee" : danger ? "#c4b5fd" : "rgba(255,255,255,0.75)",
    transition: "background .12s ease, border-color .12s ease",
  }),
  warn: {
    display: "inline-block", margin: "0 0 10px", padding: "6px 12px",
    borderRadius: 8, fontSize: "0.78rem", fontWeight: 600,
    background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.45)",
    color: "#fbbf24",
  },
  suggestWrap: { display: "flex", gap: 6, flexWrap: "wrap", padding: "6px 0 2px" },
  suggestPill: {
    background: "rgba(139,92,246,0.12)", border: "1px solid rgba(139,92,246,0.4)",
    color: "#c4b5fd", borderRadius: 999, padding: "4px 12px",
    fontSize: "0.76rem", fontWeight: 600, cursor: "pointer",
  },
  assigneeRow: {
    display: "flex", alignItems: "flex-start", gap: 10, flexWrap: "wrap",
    padding: "8px 10px", borderRadius: 12, marginBottom: 8,
    background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)",
  },
  /* Active button keeps a PERMANENT 2px cyan ring (box-shadow, so the button
     doesn't shift size), whatever view chip is currently showing. */
  assigneeBtn: (color, viewing, active) => ({
    display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
    padding: "8px 18px", borderRadius: 999, cursor: "pointer", flexShrink: 0,
    background: viewing ? `${color}2e` : `${color}14`,
    border: `1px solid ${viewing ? color : `${color}66`}`,
    color,
    boxShadow: active ? `0 0 0 2px ${ACTIVE_RING}` : "none",
    transition: "background .12s ease, border-color .12s ease, box-shadow .12s ease",
  }),
  numWrap: { flex: 1, minWidth: 150, display: "flex", flexDirection: "column", gap: 3 },
  numInput: (issue, filled, color) => ({
    width: "100%", boxSizing: "border-box",
    background: "rgba(255,255,255,0.05)",
    border: `1px solid ${issue ? "#f87171" : filled ? `${color}66` : "rgba(255,255,255,0.12)"}`,
    borderRadius: 10, padding: "9px 12px", color: "#e2e8f0", fontSize: "0.85rem", outline: "none",
  }),
  numIssue: { fontSize: "0.68rem", fontWeight: 600, color: "#f87171", paddingLeft: 2 },
  removeX: {
    background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.35)",
    color: "#f87171", borderRadius: 8, width: 28, height: 28, flexShrink: 0,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", fontSize: "0.95rem", lineHeight: 1, marginTop: 6,
  },
  card: {
    background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12, padding: 12, marginTop: 12,
  },
  colHead: {
    fontSize: "0.62rem", letterSpacing: "0.05em", textTransform: "uppercase",
    color: "rgba(255,255,255,0.45)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  },
  rowName: { fontSize: "0.85rem", fontWeight: 700, color: "#22d3ee", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  cell: { fontSize: "0.84rem", color: "#e2e8f0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  statusBadge: { display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700 },
  empty: { padding: 24, textAlign: "center", color: "rgba(255,255,255,0.5)", fontStyle: "italic", fontSize: "0.85rem" },
  hint: { marginTop: 10, fontSize: "0.72rem", color: "rgba(255,255,255,0.4)", textAlign: "center" },
  errText: { color: "#f87171", fontSize: "0.78rem", margin: "8px 2px 0" },
  saveBtn: (disabled) => ({
    width: "100%", padding: 11, borderRadius: 10, border: "none",
    background: "linear-gradient(90deg, #22d3ee, #3b82f6)", color: "#0f172a",
    fontWeight: 700, cursor: disabled ? "wait" : "pointer",
    opacity: disabled ? 0.6 : 1, margin: "12px 0 4px",
  }),
  closeBtn: {
    width: "100%", padding: 11, borderRadius: 10, border: "none",
    background: "rgba(255,255,255,0.06)", color: "#e2e8f0", fontWeight: 600,
    cursor: "pointer", margin: "0 0 4px",
  },
};

const StatusBadge = ({ status }) => {
  const ok = String(status).toLowerCase() === "ok";
  const st = ok
    ? { background: "rgba(34,197,94,0.15)", color: "#4ade80", border: "1px solid rgba(34,197,94,0.4)" }
    : { background: "rgba(239,68,68,0.15)", color: "#f87171", border: "1px solid rgba(239,68,68,0.4)" };
  return <span style={{ ...S.statusBadge, ...st }}>{status || "—"}</span>;
};

const CheckBox = ({ checked, color, onToggle }) => (
  <button
    type="button"
    onClick={(e) => { e.stopPropagation(); onToggle(); }}
    aria-label={checked ? "Unassign record" : "Assign record"}
    style={{
      width: 20, height: 20, borderRadius: 6, cursor: "pointer", padding: 0,
      border: `2px solid ${checked ? color : "rgba(255,255,255,0.28)"}`,
      background: checked ? `${color}26` : "transparent",
      color, display: "inline-flex", alignItems: "center", justifyContent: "center",
      transition: "border-color .12s ease, background .12s ease",
    }}
  >
    {checked && (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    )}
  </button>
);

const ReassigningModalTestnTag = ({ rows, allRows, onClose, onSaved, notify }) => {
  /* staff pool for the autocomplete — [{ name, job }] */
  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(true);
  const [staffQuery, setStaffQuery] = useState("");

  /* assignees picked from the autocomplete — [{ key, name, job, licenceNumber }] */
  const [assignees, setAssignees] = useState([]);
  const [activeKey, setActiveKey] = useState(null);   // receives new checks

  /* recordId -> assignee key */
  const [assignments, setAssignments] = useState({});

  /* 'filtered' | 'remaining' | 'assigned' | <assignee key> */
  const [view, setView] = useState("filtered");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  /* Auto-dismissing in-modal warning ("pick a staff member first", etc.) */
  const [warn, setWarn] = useState("");
  const warnTimerRef = useRef(null);
  const flashWarn = (message) => {
    setWarn(message);
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    warnTimerRef.current = setTimeout(() => setWarn(""), 2500);
  };
  useEffect(() => () => { if (warnTimerRef.current) clearTimeout(warnTimerRef.current); }, []);

  /* ── load the staff pool once on open ── */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await fetchTestNTagStaff();
        if (alive) setStaff(list || []);
      } catch (e) {
        if (alive) setError(e.message || "Failed to load staff list");
      } finally {
        if (alive) setStaffLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── escape closes (unless saving) ── */
  useEffect(() => {
    const handleKey = (e) => e.key === "Escape" && !saving && onClose();
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [saving, onClose]);

  const colorFor = (key) => {
    const i = assignees.findIndex((a) => a.key === key);
    return COLORS[(i < 0 ? 0 : i) % COLORS.length];
  };

  const activeAssignee = assignees.find((a) => a.key === activeKey) || null;

  /* ── existing licence-number ownership from the FULL register ──
     Map: number -> Set of Licence names that currently use it.
     Built from allRows (the page's allItems = get_testNtag_data output),
     falling back to the filtered rows if allRows wasn't passed. */
  const numberOwners = useMemo(() => {
    const source = allRows && allRows.length ? allRows : rows;
    const map = new Map();
    (source || []).forEach((r) => {
      const num = String(r[F.licenceNumber] ?? "").trim();
      const name = String(r[F.licence] ?? "").trim();
      if (!num) return;
      if (!map.has(num)) map.set(num, new Set());
      if (name) map.get(num).add(name);
    });
    return map;
  }, [allRows, rows]);

  /* Live validation for one assignee's licence number.
     Returns a message string, or null when the number is fine / empty. */
  const numberIssueFor = (a) => {
    const n = a.licenceNumber.trim();
    if (!n) return null; // "required" is enforced on Save, not while typing
    const dupInModal = assignees.some((b) => b.key !== a.key && b.licenceNumber.trim() === n);
    if (dupInModal) return "Same number as another staff member above";
    const owners = numberOwners.get(n);
    if (owners) {
      const others = [...owners].filter((x) => x !== a.name);
      if (others.length) return `Already in use by ${others.join(" / ")}`;
    }
    return null;
  };

  /* ── counts ── */
  const counts = useMemo(() => {
    let assigned = 0;
    rows.forEach((r) => { if (assignments[r[F.id]] !== undefined) assigned += 1; });
    return { filtered: rows.length, assigned, remaining: rows.length - assigned };
  }, [rows, assignments]);

  const countFor = (key) =>
    rows.reduce((n, r) => (assignments[r[F.id]] === key ? n + 1 : n), 0);

  /* ── table rows for the current view ── */
  const visibleRows = useMemo(() => {
    if (view === "remaining") return rows.filter((r) => assignments[r[F.id]] === undefined);
    if (view === "assigned") return rows.filter((r) => assignments[r[F.id]] !== undefined);
    if (view !== "filtered") return rows.filter((r) => assignments[r[F.id]] === view);
    return rows;
  }, [rows, assignments, view]);

  /* ── staff autocomplete suggestions ── */
  const suggestions = useMemo(() => {
    const q = staffQuery.trim().toLowerCase();
    if (!q) return [];
    return staff
      .filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          String(p.job ?? "").toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [staff, staffQuery]);

  /* ── actions ── */
  const addAssignee = (p) => {
    const key = staffKey(p);
    setAssignees((prev) =>
      prev.some((a) => a.key === key) ? prev : [...prev, { key, name: p.name, job: p.job ?? "", licenceNumber: "" }]
    );
    setActiveKey(key);
    setStaffQuery("");
    setError("");
  };

  const removeAssignee = (key) => {
    setAssignees((prev) => prev.filter((a) => a.key !== key));
    setAssignments((prev) => {
      const next = {};
      Object.entries(prev).forEach(([id, k]) => { if (k !== key) next[id] = k; });
      return next;
    });
    if (activeKey === key) setActiveKey(null);
    if (view === key) setView("filtered");
  };

  const toggleRecord = (id) => {
    if (assignments[id] !== undefined) {
      setAssignments((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      return;
    }
    if (!activeKey) {
      flashWarn("Pick a staff member from the search first.");
      return;
    }
    setAssignments((prev) => ({ ...prev, [id]: activeKey }));
  };

  /* Select All — toggle:
     - if every visible row is already checked for the ACTIVE staff member,
       clicking again unchecks them all (deselect);
     - otherwise it checks every visible row for the active staff member. */
  const selectAllVisible = () => {
    if (!activeKey) {
      flashWarn("Pick a staff member from the search first.");
      return;
    }
    if (!visibleRows.length) {
      flashWarn("No records in the current view.");
      return;
    }
    const allMine = visibleRows.every((r) => assignments[r[F.id]] === activeKey);
    setAssignments((prev) => {
      const next = { ...prev };
      if (allMine) {
        visibleRows.forEach((r) => { delete next[r[F.id]]; });
      } else {
        visibleRows.forEach((r) => { next[r[F.id]] = activeKey; });
      }
      return next;
    });
  };

  /* Clicking a staff total-button — view their checked records + make active.
     Clicking the same button again returns the view to Filtered, but the
     button KEEPS the cyan active ring — new checks still go to them. */
  const clickAssigneeBtn = (key) => {
    setActiveKey(key);
    setView((prev) => (prev === key ? "filtered" : key));
  };

  const setLicenceNumber = (key, value) =>
    setAssignees((prev) => prev.map((a) => (a.key === key ? { ...a, licenceNumber: value } : a)));

  /* ── save ── */
  const save = async () => {
    const payload = assignees
      .map((a) => ({
        licence: a.name,
        licenceNumber: a.licenceNumber.trim(),
        recordIds: rows.filter((r) => assignments[r[F.id]] === a.key).map((r) => r[F.id]),
      }))
      .filter((p) => p.recordIds.length > 0);

    if (!payload.length) {
      setError("Nothing to save — check at least one record against a staff member.");
      return;
    }

    /* only staff members who actually have checked records are validated */
    const withRecords = assignees.filter((a) =>
      rows.some((r) => assignments[r[F.id]] === a.key)
    );

    /* 1 — every one of them needs a number */
    const missing = withRecords.filter((a) => !a.licenceNumber.trim());
    if (missing.length) {
      setError(`Enter a licence number for: ${missing.map((a) => a.name).join(", ")}`);
      return;
    }

    /* 2 — numbers must be unique BETWEEN the staff members in the modal */
    const numCount = new Map();
    withRecords.forEach((a) => {
      const n = a.licenceNumber.trim();
      numCount.set(n, (numCount.get(n) || 0) + 1);
    });
    const dupes = [...numCount.entries()].filter(([, c]) => c > 1).map(([n]) => n);
    if (dupes.length) {
      setError(`Each staff member needs a different licence number — ${dupes.join(", ")} is used more than once.`);
      return;
    }

    /* 3 — a number may not already belong to a DIFFERENT name in the register
       (same name re-using their own existing number is fine) */
    const conflicts = withRecords
      .map((a) => {
        const owners = numberOwners.get(a.licenceNumber.trim());
        if (!owners) return null;
        const others = [...owners].filter((x) => x !== a.name);
        return others.length
          ? `${a.licenceNumber.trim()} already belongs to ${others.join(" / ")} (entered for ${a.name})`
          : null;
      })
      .filter(Boolean);
    if (conflicts.length) {
      setError(`Licence number already in use — ${conflicts.join("; ")}`);
      return;
    }

    setError("");
    setSaving(true);
    try {
      await reassignTestNTag(payload);
      const total = payload.reduce((n, p) => n + p.recordIds.length, 0);
      notify({
        message: `Reassigned ${total} record${total === 1 ? "" : "s"} to ${payload.length} staff member${payload.length === 1 ? "" : "s"}.`,
        type: "success",
      });
      if (onSaved) await onSaved();
      onClose();
    } catch (e) {
      setError(e.message || "Reassign failed");
    } finally {
      setSaving(false);
    }
  };

  const viewLabel =
    view === "filtered" ? "Filtered records"
      : view === "remaining" ? "Remaining (unchecked)"
      : view === "assigned" ? "Assigned (checked)"
      : `Assigned to ${assignees.find((a) => a.key === view)?.name ?? "—"}`;

  /* Is every visible row already the active staff member's? (drives the
     Select All label so the toggle is obvious.) */
  const allVisibleMine =
    activeKey != null &&
    visibleRows.length > 0 &&
    visibleRows.every((r) => assignments[r[F.id]] === activeKey);

  return (
    <div style={S.backdrop} onClick={() => !saving && onClose()}>
      <style>{`
        .rat-row { transition: background .12s ease; }
        .rat-row:hover { background: rgba(34,211,238,0.07); }
      `}</style>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.panelAccent} />

        <div style={S.head}>
          <h4 style={S.title}>Reassign TestNTag Records</h4>
          <button type="button" style={S.closeX} onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* 1 — chips: Select All (toggle action) + view filters */}
        <div style={S.chipRow}>
          <button
            type="button"
            style={S.chip(false, true)}
            onClick={selectAllVisible}
            title={activeAssignee
              ? allVisibleMine
                ? `Uncheck every record in the table for ${activeAssignee.name}`
                : `Check every record in the table for ${activeAssignee.name}`
              : "Pick a staff member first"}
          >
            {allVisibleMine ? "Deselect All" : "Select All"}
          </button>
          <button type="button" style={S.chip(view === "remaining")} onClick={() => setView("remaining")}>
            Remaining · {counts.remaining}
          </button>
          <button type="button" style={S.chip(view === "assigned")} onClick={() => setView("assigned")}>
            Assigned · {counts.assigned}
          </button>
          <button type="button" style={S.chip(view === "filtered")} onClick={() => setView("filtered")}>
            Filtered · {counts.filtered}
          </button>
        </div>

        {/* auto-dismissing in-modal warning */}
        {warn && <div style={S.warn}>{warn}</div>}

        {/* 2 — staff autocomplete */}
        <SearchInput
          value={staffQuery}
          onChange={setStaffQuery}
          placeholder={staffLoading ? "Loading staff…" : "Search staff name to reassign to…"}
        />
        {suggestions.length > 0 && (
          <div style={S.suggestWrap}>
            {suggestions.map((p) => (
              <button key={staffKey(p)} type="button" style={S.suggestPill} onClick={() => addAssignee(p)}>
                {p.name}{p.job ? ` (${p.job})` : ""}
              </button>
            ))}
          </div>
        )}

        {/* 3 — dynamic assignee buttons + new licence number fields */}
        {assignees.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ ...S.eyebrow, margin: "0 2px 8px" }}>
              Reassigning to
              {activeAssignee && (
                <span style={{ color: "rgba(255,255,255,0.45)", textTransform: "none", letterSpacing: 0, fontWeight: 600, marginLeft: 8 }}>
                  — new checks go to {activeAssignee.name}
                </span>
              )}
            </div>
            {assignees.map((a) => {
              const color = colorFor(a.key);
              const n = countFor(a.key);
              const issue = numberIssueFor(a);
              return (
                <div key={a.key} style={S.assigneeRow}>
                  <button
                    type="button"
                    style={S.assigneeBtn(color, view === a.key, activeKey === a.key)}
                    onClick={() => clickAssigneeBtn(a.key)}
                    title={`Show the ${n} record${n === 1 ? "" : "s"} checked for ${a.name}`}
                  >
                    <span style={{ fontSize: "0.8rem", fontWeight: 700, whiteSpace: "nowrap" }}>
                      {a.name}{a.job ? ` (${a.job})` : ""}
                    </span>
                    <span style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", opacity: 0.85, whiteSpace: "nowrap" }}>
                      {n} record{n === 1 ? "" : "s"} assigned
                    </span>
                  </button>
                  <div style={S.numWrap}>
                    <input
                      type="text"
                      style={S.numInput(!!issue, !!a.licenceNumber.trim(), color)}
                      placeholder="New licence number…"
                      value={a.licenceNumber}
                      onChange={(e) => setLicenceNumber(a.key, e.target.value)}
                      title={issue || `Licence number for ${a.name}`}
                    />
                    {issue && <span style={S.numIssue}>{issue}</span>}
                  </div>
                  <button
                    type="button"
                    style={S.removeX}
                    title={`Remove ${a.name} and uncheck their records`}
                    onClick={() => removeAssignee(a.key)}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* 4 — table (auto-populated from the TestNTag filtered records) */}
        <div style={S.card}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ ...S.eyebrow, color: view !== "filtered" && view !== "remaining" && view !== "assigned" ? colorFor(view) : "#22d3ee" }}>
              {viewLabel}
            </span>
            <span style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.45)" }}>
              {visibleRows.length} record{visibleRows.length === 1 ? "" : "s"}
            </span>
          </div>

          {visibleRows.length === 0 ? (
            <div style={S.empty}>No records in this view.</div>
          ) : (
            <div style={{ maxHeight: 340, overflowY: "auto", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <div style={{ minWidth: 640 }}>
                <div
                  style={{
                    display: "grid", gridTemplateColumns: TABLE_COLS, alignItems: "end",
                    padding: "8px 8px 10px", borderBottom: "1px solid rgba(255,255,255,0.12)",
                    columnGap: 8, position: "sticky", top: 0, zIndex: 1, background: "#1b2537",
                  }}
                >
                  <span style={S.colHead}> </span>
                  <span style={S.colHead}>Client</span>
                  <span style={S.colHead}>Item</span>
                  <span style={S.colHead}>Licence</span>
                  <span style={{ ...S.colHead, textAlign: "center" }}>Lead Status</span>
                  <span style={{ ...S.colHead, textAlign: "center" }}>Unit Status</span>
                </div>

                {visibleRows.map((r, i) => {
                  const id = r[F.id];
                  const assignedTo = assignments[id];
                  const checked = assignedTo !== undefined;
                  const color = checked ? colorFor(assignedTo) : (activeKey ? colorFor(activeKey) : "#22d3ee");
                  return (
                    <div
                      key={id || i}
                      className="rat-row"
                      onClick={() => toggleRecord(id)}
                      style={{
                        display: "grid", gridTemplateColumns: TABLE_COLS, alignItems: "center",
                        padding: "10px 8px", cursor: "pointer", borderRadius: 8, columnGap: 8,
                        borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none",
                        background: checked ? `${colorFor(assignedTo)}0d` : "transparent",
                      }}
                      title={checked
                        ? `Checked for ${assignees.find((a) => a.key === assignedTo)?.name ?? "—"} — click to uncheck`
                        : activeAssignee
                          ? `Click to assign to ${activeAssignee.name}`
                          : "Pick a staff member first"}
                    >
                      <span>
                        <CheckBox checked={checked} color={color} onToggle={() => toggleRecord(id)} />
                      </span>
                      <span style={S.rowName}>{r[F.client] || "—"}</span>
                      <span style={S.cell}>{r[F.item] || "—"}</span>
                      <span style={S.cell}>{r[F.licence] || "—"}</span>
                      <span style={{ textAlign: "center" }}><StatusBadge status={r[F.leadStatus]} /></span>
                      <span style={{ textAlign: "center" }}><StatusBadge status={r[F.unitStatus]} /></span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={S.hint}>
            Check records for the active staff member (cyan ring), then Save to update their Licence name and number.
          </div>
        </div>

        {error && <div style={S.errText}>{error}</div>}

        {/* 5 — save / close */}
        <button type="button" style={S.saveBtn(saving)} disabled={saving} onClick={save}>
          {saving ? "Saving…" : `Save Reassignments · ${counts.assigned}`}
        </button>
        <button type="button" style={S.closeBtn} onClick={onClose} disabled={saving}>Close</button>
      </div>
    </div>
  );
};

export default ReassigningModalTestnTag;